import { useState, useRef } from 'react'
import { dialog, geo, preview as platformPreview } from '@carrent/platform'
import { useNavigate } from 'react-router-dom'
import { api } from '@carrent/api'
import { useAuthStore } from '@carrent/core'
import DamageStep from './DamageStep'
import { enqueueStartTrip, flushNow } from '../../offline/tripTasks'

// Конфигурация шагов фото
const PHOTO_STEPS = [
  { key: 'front',    label: 'Спереди',     hint: 'Сфотографируйте автомобиль спереди — капот, фары, бампер',    icon: '🚗', emoji: '⬆️' },
  { key: 'back',     label: 'Сзади',       hint: 'Задний бампер, фонари, номерной знак',                         icon: '🚗', emoji: '⬇️' },
  { key: 'left',     label: 'Левый бок',   hint: 'Левая сторона целиком — от фары до фонаря',                    icon: '🚗', emoji: '⬅️' },
  { key: 'right',    label: 'Правый бок',  hint: 'Правая сторона целиком — от фары до фонаря',                   icon: '🚗', emoji: '➡️' },
  { key: 'interior', label: 'Салон',       hint: 'Общий вид салона с водительского сиденья',                     icon: '🪑', emoji: '🪑' },
  { key: 'odometer', label: 'Одометр',     hint: 'Чёткое фото приборной панели с показаниями пробега',           icon: '🔢', emoji: '🔢' },
]

function PhotoStep({ step, photo, onPhoto }) {
  // Ключевой фикс: превью вычисляется из prop photo, а не из локального state.
  // Это значит при возврате назад фото восстанавливается автоматически,
  // потому что photos[key] в родителе не сбрасывается.
  const inputRef = useRef()
  const preview = photo ? platformPreview.objectUrl(photo) : null

  const handleFile = (e) => {
    const file = e.target.files[0]
    if (!file) return
    onPhoto(file)
    // сбросить value чтобы onChange сработал повторно при той же картинке
    e.target.value = ''
  }

  const handleRetake = () => {
    // Сначала сбрасываем фото в родителе
    onPhoto(null)
    // Затем открываем камеру через небольшую задержку
    // (нужно чтобы React успел перерендерить и input стал видимым)
    setTimeout(() => inputRef.current?.click(), 50)
  }

  return (
    <div className="flex-col" style={{ gap: 'var(--gap)', flex: 1 }}>
      {/* Инструкция */}
      <div style={{
        background: 'rgba(59,130,246,0.08)', border: '1px solid rgba(59,130,246,0.25)',
        borderRadius: 'var(--r)', padding: 'var(--gap)',
      }}>
        <div style={{ fontSize: '2.5rem', textAlign: 'center', marginBottom: 8 }}>
          {step.emoji}
        </div>
        <p style={{ fontSize: '0.88rem', color: 'var(--accent)', textAlign: 'center', lineHeight: 1.6 }}>
          {step.hint}
        </p>
      </div>

      {/* Превью / кнопка съёмки */}
      {preview ? (
        <div style={{ position: 'relative' }}>
          <img src={preview} alt={step.label}
            style={{ width: '100%', borderRadius: 'var(--r)', maxHeight: 280, objectFit: 'cover',
              border: '2px solid var(--success)' }} />
          <div style={{ position: 'absolute', top: 8, right: 8 }}>
            <button onClick={handleRetake}
              style={{ background: 'rgba(0,0,0,0.7)', color: '#fff', border: 'none',
                borderRadius: 'var(--r-sm)', padding: '6px 12px', cursor: 'pointer',
                fontSize: '0.82rem' }}>
              🔄 Переснять
            </button>
          </div>
          <div style={{ position: 'absolute', bottom: 8, left: 8,
            background: 'rgba(34,197,94,0.9)', color: '#fff',
            padding: '4px 12px', borderRadius: 99, fontSize: '0.78rem', fontWeight: 700 }}>
            ✓ Фото сделано
          </div>
        </div>
      ) : (
        <button onClick={() => inputRef.current?.click()} style={{
          height: 220, border: '2px dashed var(--border)', borderRadius: 'var(--r)',
          background: 'var(--bg-card)', cursor: 'pointer', display: 'flex',
          flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          gap: 12, transition: 'border-color 0.15s, background 0.15s',
          color: 'var(--text-muted)',
        }}>
          <span style={{ fontSize: '3rem' }}>📷</span>
          <span style={{ fontSize: '0.9rem' }}>Нажмите для съёмки</span>
          <span style={{ fontSize: '0.78rem', opacity: 0.7 }}>Или выберите из галереи</span>
        </button>
      )}

      {/* input всегда в DOM, скрыт — это важно для корректной работы камеры */}
      <input ref={inputRef} type="file" accept="image/*" capture="environment"
        style={{ display: 'none' }} onChange={handleFile} />
    </div>
  )
}


// ── Блок дополнительных фото ──────────────────────────────────────────────────
function ExtraPhotos({ extras, onAdd, onRemove }) {
  const inputRef = useRef()

  const handleFile = (e) => {
    const files = Array.from(e.target.files)
    files.forEach(f => onAdd(f))
    e.target.value = ''
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <label className="field__label" style={{ margin: 0 }}>
          Дополнительные фото
          <span className="text-muted" style={{ fontWeight: 400, marginLeft: 6, fontSize: '0.75rem' }}>
            (царапины, сколы, потёртости)
          </span>
        </label>
        <button type="button" onClick={() => inputRef.current?.click()}
          className="btn btn--outline"
          style={{ padding: '4px 12px', fontSize: '0.8rem', flexShrink: 0 }}>
          + Добавить
        </button>
      </div>

      {extras.length === 0 ? (
        <button type="button" onClick={() => inputRef.current?.click()} style={{
          width: '100%', height: 80, border: '1px dashed var(--border)',
          borderRadius: 'var(--r)', background: 'transparent', cursor: 'pointer',
          color: 'var(--text-muted)', fontSize: '0.85rem', display: 'flex',
          alignItems: 'center', justifyContent: 'center', gap: 8,
        }}>
          <span style={{ fontSize: '1.3rem' }}>📎</span>
          Добавьте фото дефектов, которые хотите зафиксировать
        </button>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6 }}>
          {extras.map(ex => (
            <div key={ex.id} style={{ position: 'relative', aspectRatio: '4/3' }}>
              <img src={ex.preview} alt="доп. фото"
                style={{ width: '100%', height: '100%', objectFit: 'cover',
                  borderRadius: 'var(--r-sm)', border: '1px solid var(--border)' }} />
              <button type="button" onClick={() => onRemove(ex.id)} style={{
                position: 'absolute', top: 3, right: 3,
                width: 22, height: 22, borderRadius: '50%',
                background: 'rgba(239,68,68,0.85)', color: '#fff',
                border: 'none', cursor: 'pointer', fontSize: '0.75rem',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                lineHeight: 1,
              }}>✕</button>
            </div>
          ))}
          <button type="button" onClick={() => inputRef.current?.click()} style={{
            aspectRatio: '4/3', border: '1px dashed var(--border)',
            borderRadius: 'var(--r-sm)', background: 'transparent', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: 'var(--text-muted)', fontSize: '1.5rem',
          }}>+</button>
        </div>
      )}

      <input ref={inputRef} type="file" accept="image/*" capture="environment"
        multiple style={{ display: 'none' }} onChange={handleFile} />
    </div>
  )
}

export default function StartTripWizard({ car, onCancel, onStarted }) {
  const { refreshUser } = useAuthStore()
  const navigate = useNavigate()

  // Шаги: 0..5 = фото, 6 = одометр+данные, 7 = подтверждение
  const [step, setStep]       = useState(0)
  const [photos, setPhotos]   = useState({})  // key → File
  const [mileage, setMileage] = useState(car.mileage?.toString() || '')
  const [extras, setExtras]   = useState([])  // {id, file, preview}
  const addExtra    = (file) => setExtras(ex => [...ex, { id: Date.now(), file, preview: platformPreview.objectUrl(file) }])
  const removeExtra = (id)  => setExtras(ex => ex.filter(e => e.id !== id))
  const [comment, setComment] = useState('')
  const [damageMarks, setDamageMarks] = useState([])
  const [queued, setQueued] = useState(false)
  // Идентификатор черновика переживает перезагрузку страницы:
  // на нём держатся и снимки в хранилище, и задача в очереди.
  const [draftId] = useState(() =>
    globalThis.crypto?.randomUUID?.().replace(/[^a-zA-Z0-9-]/g, '')
    ?? `d-${Date.now()}-${Math.random().toString(36).slice(2)}`)
  const [loading, setLoading] = useState(false)
  const [err, setErr]         = useState('')

  const totalPhotoSteps = PHOTO_STEPS.length
  const isPhotoStep     = step < totalPhotoSteps
  const isDamageStep    = step === totalPhotoSteps
  const isDataStep      = step === totalPhotoSteps + 1
  const isConfirmStep   = step === totalPhotoSteps + 2

  const currentPhotoStep = PHOTO_STEPS[step]

  const setPhoto = (key, file) => setPhotos(p => ({ ...p, [key]: file }))

  const canGoNext = () => {
    if (isPhotoStep)  return !!photos[currentPhotoStep.key]
    // Ноль точек — валидное состояние: машина бывает целой.
    if (isDamageStep) return true
    if (isDataStep)   return mileage.trim() !== ''
    return true
  }

  const goNext = () => {
    setErr('')
    if (step < totalPhotoSteps + 2) setStep(s => s + 1)
  }

  const submit = async () => {
    // Критичное повреждение на приёмке — повод остановиться и решить,
    // а не строка в акте, которую заметят через неделю.
    const critical = damageMarks.filter(m => m.severity === 'critical')
    if (critical.length) {
      const proceed = await dialog.confirm(
        `Отмечено критичных повреждений: ${critical.length}. ` +
        'С такими ехать нельзя. Всё равно начать поездку?')
      if (!proceed) return
    }

    setLoading(true); setErr('')

    // Сначала на устройство, потом в очередь, и только потом попытка
    // отправки. Обрыв связи теперь стоит одной попытки, а не всей
    // приёмки: снимки и отметки уже сохранены.
    try {
      await enqueueStartTrip({
        draftId,
        carId: car.id,
        startMileage: mileage,
        comment,
        photoMap: photos,
        extras,
        marks: damageMarks,
        // Координата снимается перед самой отправкой: пока водитель
        // фотографировал шесть ракурсов, телефон успел поймать фикс.
        // Отказ или таймаут вернут source: 'none', и приёмка пройдёт.
        geo: await geo.getPositionSmart(),
      })
    } catch (e) {
      setErr('Не удалось сохранить приёмку на устройстве. Освободите место и попробуйте снова.')
      setLoading(false)
      return
    }

    // При живой сети водитель должен увидеть обычный переход
    // в поездку, а не «отправится позже».
    try {
      await flushNow()
      // Источник истины — сервер, а не результат разгрузки: задача
      // могла уйти и раньше, с прошлой попытки или из другой вкладки.
      const { data } = await api.get('/trips/active')
      if (data?.data) {
        onStarted(data.data)
        return
      }
    } catch {
      // Сети нет или сервер недоступен — задача осталась в очереди.
    }

    // Сеть не дала отправить. Работа не потеряна — задача в очереди,
    // и об этом надо сказать прямо, а не оставить спиннер.
    setLoading(false)
    setQueued(true)
  }

  // Прогресс
  const totalSteps  = totalPhotoSteps + 3
  const progressPct = Math.round((step / (totalSteps - 1)) * 100)

  if (queued) {
    return (
      <div className="queued-screen">
        <h2 className="queued-screen__title">Приёмка сохранена</h2>
        <p className="queued-screen__text">
          Сети сейчас нет. Фотографии и отметки повреждений лежат
          на устройстве и уйдут сами, как только связь появится.
          Закрывать приложение можно — работа не потеряется.
        </p>
        <p className="queued-screen__hint">
          Следить за отправкой можно в разделе «Ещё» → «Ожидают отправки».
        </p>
        <button className="btn btn--primary btn--full" onClick={onCancel}>
          Понятно
        </button>
      </div>
    )
  }

  return (
    <div style={{ minHeight: '100dvh', display: 'flex', flexDirection: 'column',
      background: 'var(--bg)', paddingBottom: 'env(safe-area-inset-bottom, 20px)' }}>

      {/* Шапка */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--gap-sm)',
        padding: 'var(--gap)', paddingTop: 'calc(var(--gap) + env(safe-area-inset-top, 0px))',
        borderBottom: '1px solid var(--border)' }}>
        <button onClick={onCancel}
          style={{ background: 'none', border: 'none', color: 'var(--text)',
            fontSize: '1.5rem', cursor: 'pointer', padding: '4px' }}>‹</button>
        <div style={{ flex: 1 }}>
          <p style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>
            Осмотр перед поездкой
          </p>
          <p style={{ fontWeight: 600, fontSize: '0.95rem' }}>
            {car.brand} {car.model} · {car.plate}
          </p>
        </div>
        <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>
          {step + 1}/{totalSteps}
        </span>
      </div>

      {/* Прогресс-бар */}
      <div style={{ height: 3, background: 'var(--border)' }}>
        <div style={{ height: '100%', background: 'var(--accent)',
          width: `${progressPct}%`, transition: 'width 0.3s' }} />
      </div>

      {/* Индикаторы фото */}
      <div style={{ display: 'flex', gap: 4, padding: '8px var(--gap)' }}>
        {PHOTO_STEPS.map((s, i) => (
          <div key={s.key} style={{
            flex: 1, height: 4, borderRadius: 99,
            background: photos[s.key] ? 'var(--success)'
              : i === step ? 'var(--accent)' : 'var(--border)',
            transition: 'background 0.2s',
          }} />
        ))}
      </div>

      {/* Контент */}
      <div style={{ flex: 1, padding: '0 var(--gap)', display: 'flex', flexDirection: 'column' }}>

        {isPhotoStep && (
          <>
            <h2 style={{ fontSize: '1.3rem', marginBottom: 'var(--gap-sm)' }}>
              {currentPhotoStep.label}
            </h2>
            <PhotoStep
              step={currentPhotoStep}
              photo={photos[currentPhotoStep.key]}
              onPhoto={(f) => setPhoto(currentPhotoStep.key, f)}
            />
          </>
        )}

        {isDamageStep && (
          <>
            <h2 className="damage-step__title">Повреждения</h2>
            <DamageStep car={car} marks={damageMarks} onChange={setDamageMarks} />
          </>
        )}

        {isDataStep && (
          <div className="flex-col" style={{ gap: 'var(--gap)', paddingTop: 'var(--gap-sm)' }}>
            <h2 style={{ fontSize: '1.3rem' }}>Показания одометра</h2>
            <div className="alert alert--info">
              Введите текущий пробег по одометру. Он зафиксируется как начальный.
            </div>
            <div className="field">
              <label className="field__label">
                Пробег (км) <span style={{ color: 'var(--danger)' }}>*</span>
              </label>
              <input className="input" type="number" inputMode="numeric"
                style={{ fontSize: '1.4rem', fontWeight: 700, textAlign: 'center', height: 64 }}
                placeholder={car.mileage?.toString() || '0'}
                value={mileage} onChange={e => setMileage(e.target.value)} />
              {car.mileage > 0 && (
                <p className="text-muted" style={{ fontSize: '0.78rem', marginTop: 4 }}>
                  По данным системы: {car.mileage.toLocaleString()} км
                </p>
              )}
            </div>
            <div className="field">
              <label className="field__label">Комментарий перед выездом</label>
              <textarea className="input" rows={3}
                placeholder="Опишите замеченные особенности, вмятины, царапины..."
                value={comment} onChange={e => setComment(e.target.value)}
                style={{ resize: 'vertical' }} />
            </div>

            <ExtraPhotos extras={extras} onAdd={addExtra} onRemove={removeExtra} />
          </div>
        )}

        {isConfirmStep && (
          <div className="flex-col" style={{ gap: 'var(--gap)', paddingTop: 'var(--gap-sm)' }}>
            <h2 style={{ fontSize: '1.3rem' }}>Всё готово?</h2>

            {/* Сетка фото превью */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 4 }}>
              {PHOTO_STEPS.map(s => (
                <div key={s.key} style={{ position: 'relative', aspectRatio: '4/3' }}>
                  <img src={platformPreview.objectUrl(photos[s.key])} alt={s.label}
                    style={{ width: '100%', height: '100%', objectFit: 'cover',
                      borderRadius: 'var(--r-sm)' }} />
                  <div style={{ position: 'absolute', bottom: 2, left: 2, right: 2,
                    background: 'rgba(0,0,0,0.6)', borderRadius: 4,
                    padding: '1px 4px', fontSize: '0.62rem', color: '#fff', textAlign: 'center' }}>
                    {s.label}
                  </div>
                </div>
              ))}
              {extras.map((ex, i) => (
                <div key={ex.id} style={{ position: 'relative', aspectRatio: '4/3' }}>
                  <img src={ex.preview} alt={`доп. ${i+1}`}
                    style={{ width: '100%', height: '100%', objectFit: 'cover',
                      borderRadius: 'var(--r-sm)', border: '1px solid rgba(245,158,11,0.5)' }} />
                  <div style={{ position: 'absolute', bottom: 2, left: 2, right: 2,
                    background: 'rgba(0,0,0,0.6)', borderRadius: 4,
                    padding: '1px 4px', fontSize: '0.62rem', color: '#fff', textAlign: 'center' }}>
                    Доп. {i+1}
                  </div>
                </div>
              ))}
            </div>
            {extras.length > 0 && (
              <p className="text-muted" style={{ fontSize: '0.78rem', textAlign: 'center' }}>
                + {extras.length} дополнительных фото
              </p>
            )}

            <div className="card" style={{ padding: 'var(--gap-sm) var(--gap)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span className="text-muted" style={{ fontSize: '0.85rem' }}>Начальный пробег</span>
                <span style={{ fontWeight: 700 }}>{parseInt(mileage).toLocaleString()} км</span>
              </div>
              {comment && (
                <div style={{ marginTop: 6, fontSize: '0.82rem', color: 'var(--text-muted)' }}>
                  💬 {comment}
                </div>
              )}
            </div>

            <div className="damage-summary">
              {damageMarks.length === 0 ? (
                <span className="damage-summary__clean">Повреждений не отмечено</span>
              ) : (
                <>
                  <span className="damage-summary__count">
                    Повреждений отмечено: {damageMarks.filter(m => m.status === 'new').length}
                  </span>
                  {damageMarks.some(m => m.status === 'confirmed_existing') && (
                    <span className="damage-summary__confirmed">
                      Подтверждено прежних: {damageMarks.filter(m => m.status === 'confirmed_existing').length}
                    </span>
                  )}
                  {damageMarks.some(m => m.severity === 'critical') && (
                    <span className="damage-summary__critical">
                      Есть критичные — ехать нельзя
                    </span>
                  )}
                </>
              )}
            </div>

            {err && <div className="alert alert--error">{err}</div>}
          </div>
        )}
      </div>

      {/* Кнопки навигации */}
      <div style={{ padding: 'var(--gap)', paddingTop: 'var(--gap-sm)',
        borderTop: '1px solid var(--border)', display: 'flex', gap: 'var(--gap-sm)' }}>
        {step > 0 && !isConfirmStep && (
          <button className="btn btn--outline" style={{ flex: 1 }}
            onClick={() => setStep(s => s - 1)}>
            ← Назад
          </button>
        )}
        {!isConfirmStep ? (
          <button className="btn btn--primary" style={{ flex: 2 }}
            onClick={goNext} disabled={!canGoNext()}>
            {isDataStep ? 'Проверить →' : 'Далее →'}
          </button>
        ) : (
          <>
            <button className="btn btn--outline" style={{ flex: 1 }}
              onClick={() => setStep(s => s - 1)}>
              ← Назад
            </button>
            <button className="btn btn--primary" style={{ flex: 2, height: 52 }}
              onClick={submit} disabled={loading}>
              {loading ? <span className="spinner" /> : '🚗 Начать поездку'}
            </button>
          </>
        )}
      </div>
    </div>
  )
}
